import { faChevronDown, faChevronUp, faHouseMedical, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { EXPLAIN_PALLIATIVE } from "../../../../amplify/api-gateway/write";
import { GenerateModuleHeroV2 } from "../../../common/GenerateModuleHeroV2";
import { FakeLoadingBar } from "../common/FakeLoadingBar";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMDashboardUpdated } from "../common/LLMDashboardUpdated";
import { isSectionUnread } from "../utils";
import { SectionPalliativeButton } from "./SectionPalliativeButton";
import { SectionPalliativeDetails } from "./SectionPalliativeDetails";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";

const EXPECTED_REFRESH_TIME = 60_000;

export const SectionPalliative = ({
    isLoading,
    tenant,
    data,
    palliativeActive,
    setPalliativeActive,
    patientVisit,
    feedbacks,
    setFeedbacks,
    statuses,
    setRegenerateTarget,
    setShowContextMenu,
    handleShowScanMultiModal,
    unreadFileVersionMap = undefined,
    latestFileVersions = undefined,
    sectionToSetter = undefined,
    handleFileVersionChange = undefined,
    scoresData = undefined,
    scanMultiForCitations = undefined,
}: any) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const palliativeTitles = [
        "Palliative & ACP",
        "Goals of Care", 
        "Prognosis",
        "Legal",
        "Communication",
        "Critical Gaps",
        "Recommendations"
    ];

    const palliativeTitleToSection: { [key: string]: any } = {
        "Palliative & ACP": "palliative_acp",
        "Goals of Care": "goals_of_care",
        "Prognosis": "prognosis",
        "Legal": "legal",
        "Communication": "communication",
        "Critical Gaps": "critical_gaps",
        "Recommendations": "recommendations",
        "Unmet Needs": "unmet_needs",
    };

    const scanMulti = scanMultiForCitations;
    const formatStatusLabel = (status?: string) => {
        if (!status) return "Unknown";
        return status
            .split(/[\s_]+/)
            .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(" ");
    };

    const getBadgeVariant = (status?: string) => {
        if (!status) return "secondary";
        const normalized = status.toLowerCase();
        if (normalized === "likely" || normalized === "certain") return "warning";
        if (normalized === "possible") return "info";
        if (normalized === "unlikely") return "secondary";
        return "secondary";
    };

    const getStatusClass = (status?: string) => {
        if (!status) return "";
        const normalized = status.toLowerCase();
        if (normalized === "likely" || normalized === "certain") {
            return "orange-bg";
        }
        if (normalized === "possible") {
            return "yellow-bg";
        }
        return "";
    };

    // Helper function to prepare data for new sections
    const prepareDataForSection = (sectionKey: string, rawData: any) => {
        if (!rawData) return null;

        switch (sectionKey) {
            case 'unmet_needs':
                return {
                    summary: rawData.unmet_needs_rationale || "Assessment of unmet palliative care needs.",
                    status: rawData.unmet_needs,
                    rationale: rawData.unmet_needs_rationale,
                    items: []
                };
            default:
                return rawData[sectionKey] || null;
        }
    };

    if (!isLoading && !data) {
        return (
            <GenerateModuleHeroV2 type={EXPLAIN_PALLIATIVE} setRegenerateTarget={setRegenerateTarget} setShowContextMenu={setShowContextMenu} />
        )
    }

    return (
        <Card className="mt-2">
            <FakeLoadingBar isActive={isLoading} maxDuration={EXPECTED_REFRESH_TIME} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHouseMedical} fixedWidth /></span><strong>Palliative Care Assessment</strong> {isSectionUnread(EXPLAIN_PALLIATIVE, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        <LLMDashboardUpdated patient={patientVisit} statuses={statuses} handleShowScanMultiModal={handleShowScanMultiModal} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_PALLIATIVE} sectionToSetter={sectionToSetter} handleFileVersionChange={handleFileVersionChange} />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => { e.stopPropagation(); setRegenerateTarget(EXPLAIN_PALLIATIVE); setShowContextMenu(true) }}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <LLMDashboardFeedbackBar patientVisit={patientVisit} feedbackTarget={"palliative"} feedbacks={feedbacks} setFeedbacks={setFeedbacks} />
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => { e.stopPropagation(); setIsExpanded(!isExpanded) }}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                <Row className="mt-4 mb-4">
                    <Col>
                        <Card className={`notes-section ${getStatusClass(data?.unmet_needs)}`}>
                            <Card.Body>
                                <p className="mb-1">
                                    Unmet Needs:{" "}
                                    <Badge bg={getBadgeVariant(data?.unmet_needs)}>
                                        {formatStatusLabel(data?.unmet_needs)}
                                    </Badge>
                                </p>
                                {
                                    data && data.unmet_needs_rationale && scanMulti && (
                                        <LLMDashboardMarkdownRenderer content={data.unmet_needs_rationale} scanMulti={scanMulti} />
                                    )
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {isExpanded && (
                    <>
                        {
                            data && data["error"] ? (
                                <Row className="mt-2">
                                    <Col md={6}>
                                        A Palliative Care Assessment was unable to be created. {data['error']}
                                    </Col>
                                </Row>
                            ) : data ? (
                                <Row className="mt-2">
                                    <Col md={6}>
                                        {
                                            palliativeTitles.map((title: string, idx: number) => {
                                                const sectionKey = palliativeTitleToSection[title];
                                                const sectionData = prepareDataForSection(sectionKey, data);
                                                
                                                return (
                                                    <SectionPalliativeButton 
                                                        key={idx}
                                                        idx={idx} 
                                                        isLoading={false} 
                                                        active={palliativeActive} 
                                                        setActive={setPalliativeActive} 
                                                        data={sectionData} 
                                                        title={title} 
                                                        scanMulti={scanMulti} 
                                                    />
                                                );
                                            })
                                        }
                                    </Col>
                                    <Col md={6}>
                                        {
                                            palliativeActive < palliativeTitles.length && (
                                                (() => {
                                                    const activeTitle = palliativeTitles[palliativeActive];
                                                    const sectionKey = palliativeTitleToSection[activeTitle];
                                                    const sectionData = prepareDataForSection(sectionKey, data);
                                                    
                                                    return sectionData ? (
                                                        <SectionPalliativeDetails 
                                                            data={sectionData} 
                                                            title={activeTitle} 
                                                            scanMulti={scanMulti} 
                                                        />
                                                    ) : (
                                                        <Card>
                                                            <Card.Body>
                                                                <Row>
                                                                    <Col>
                                                                        <h5>{activeTitle}</h5>
                                                                        <p className="text-muted">No data available for this section.</p>
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    );
                                                })()
                                            )
                                        }
                                    </Col>
                                </Row>
                            ) : (
                                <></>
                            )
                        }
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
